import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, BookOpen, GraduationCap, Calendar, ScanLine, Award, Gift, Bell, Lightbulb, FileText, MessageSquareText, LogOut, FolderOpen, Headphones, Package, Trophy, FileSpreadsheet, History, Mail, BarChart3, AlertTriangle, KeyRound, Activity, UserPlus, Images } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import api from "@/lib/api";

/** Mapeia o role técnico → label amigável para exibir no UI. */
const ROLE_LABELS = {
  admin_master: "Gestor Master",
  operador_evento: "Operador de Eventos",
  comercial: "Comercial",
  aluno_validado: "Aluno",
  aluno_premium: "Aluno Premium",
  usuario_comum: "Usuário",
};

const all = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin_master"] },
  { to: "/admin/gestao-acessos", label: "Gestão de Acessos", icon: KeyRound, roles: ["admin_master"] },
  { to: "/admin/logs-acesso", label: "Logs de Acesso", icon: Activity, roles: ["admin_master"] },
  { to: "/admin/usuarios", label: "Alunos", icon: Users, roles: ["admin_master"] },
  { to: "/admin/import-alunos", label: "Importar Alunos (CSV)", icon: FileSpreadsheet, roles: ["admin_master"] },
  { to: "/admin/cursos-presenciais", label: "Cursos Presenciais", icon: GraduationCap, roles: ["admin_master"] },
  { to: "/admin/matriculas", label: "Matrículas", icon: UserPlus, roles: ["admin_master"] },
  { to: "/admin/cursos-gratuitos", label: "Aulas Gratuitas", icon: BookOpen, roles: ["admin_master"] },
  { to: "/admin/eventos", label: "Eventos / Presença", icon: Calendar, roles: ["admin_master", "operador_evento"] },
  { to: "/admin/leitor-qr", label: "Leitor QR", icon: ScanLine, roles: ["admin_master", "operador_evento"] },
  { to: "/admin/pendencias-presenca", label: "Pendências de Presença", icon: AlertTriangle, roles: ["admin_master"], badge: "pendencies" },
  { to: "/admin/certificados", label: "Certificados", icon: Award, roles: ["admin_master"] },
  { to: "/admin/modelos-certificado", label: "Modelos de Certificado", icon: Images, roles: ["admin_master"] },
  { to: "/admin/pesquisas", label: "Pesquisas & NPS", icon: BarChart3, roles: ["admin_master"] },
  { to: "/admin/modelo-pesquisa", label: "Modelo de Pesquisa", icon: FileText, roles: ["admin_master"] },
  { to: "/admin/materiais", label: "Materiais", icon: FolderOpen, roles: ["admin_master"] },
  { to: "/admin/consultorias", label: "Consultorias", icon: Headphones, roles: ["admin_master"] },
  { to: "/admin/conquistas", label: "Conquistas e Níveis", icon: Trophy, roles: ["admin_master"] },
  { to: "/admin/kits-vip", label: "Kits VIP", icon: Package, roles: ["admin_master"] },
  { to: "/admin/beneficios", label: "Convênios", icon: Gift, roles: ["admin_master"] },
  { to: "/admin/notificacoes", label: "Notificações", icon: Bell, roles: ["admin_master"] },
  { to: "/admin/sugestoes", label: "Sugestões", icon: MessageSquareText, roles: ["admin_master"] },
  { to: "/admin/leads", label: "Inscrições", icon: Users, roles: ["admin_master", "comercial"], badge: "newLeads" },
  { to: "/admin/banco-ideias", label: "Banco de PLs", icon: Lightbulb, roles: ["admin_master"] },
  { to: "/admin/auditoria", label: "Auditoria", icon: History, roles: ["admin_master"] },
  { to: "/admin/email", label: "E-mail", icon: Mail, roles: ["admin_master"] },
];

/** Hook que mantém a contagem de inscrições novas atualizada (polling a cada 30s). */
function useNewLeadsCount(user) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!user || !["admin_master", "comercial"].includes(user.role)) return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/leads/count-new");
        if (alive) setCount(Number(data?.count) || 0);
      } catch { /* silencia falhas pra não poluir UI */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30_000);
    return () => { alive = false; clearInterval(interval); };
  }, [user?.id, user?.role]);
  return count;
}

/** Hook para contagem de pendências de presença (atualiza a cada 30s). */
function usePendenciesCount(user) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!user || user.role !== "admin_master") return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/attendance/pendencies/count");
        if (alive) setCount(Number(data?.count) || 0);
      } catch { /* silencia */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30_000);
    return () => { alive = false; clearInterval(interval); };
  }, [user?.id, user?.role]);
  return count;
}

function NavList({ user, onNavigate }) {
  const newLeads = useNewLeadsCount(user);
  const pendencies = usePendenciesCount(user);
  const badges = { newLeads, pendencies };
  return (
    <nav className="flex flex-col gap-0.5 p-3" data-testid="admin-sidebar-nav">
      {all.filter(i => i.roles.includes(user?.role)).map(it => {
        const Icon = it.icon;
        const badgeValue = it.badge ? badges[it.badge] : 0;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`sidebar-${it.to.split('/').pop()}`}
            onClick={onNavigate}
            className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))] font-medium" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]"}`}
          >
            <Icon className="w-4 h-4" />
            <span className="flex-1">{it.label}</span>
            {badgeValue > 0 && (
              <span
                className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-semibold leading-none shadow-sm"
                data-testid={`sidebar-badge-${it.to.split('/').pop()}`}
                aria-label={`${badgeValue} novas`}
              >
                {badgeValue > 99 ? "99+" : badgeValue}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex lg:flex-col w-[280px] shrink-0 border-r border-[hsl(var(--border))] bg-white">
        <div className="h-16 flex items-center px-4 border-b border-[hsl(var(--border))]">
          <Logo size={28} />
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavList user={user} />
        </div>
        <div className="p-3 border-t border-[hsl(var(--border))]">
          <Button variant="outline" className="w-full justify-start" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-[hsl(var(--border))] flex items-center px-4 lg:px-6 gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px]">
              <div className="h-16 flex items-center px-4 border-b border-[hsl(var(--border))]">
                <Logo size={28} />
              </div>
              <NavList user={user} onNavigate={() => setOpen(false)} />
              <div className="p-3 border-t border-[hsl(var(--border))]">
                <Button variant="outline" className="w-full justify-start" onClick={() => { logout(); navigate("/admin/login"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <h1 className="font-display font-semibold text-lg text-[hsl(var(--primary))]" data-testid="admin-page-title">{title}</h1>
          <div className="ml-auto text-xs text-muted-foreground hidden sm:block">
            {user?.full_name} • <span>{ROLE_LABELS[user?.role] || user?.role?.replace('_', ' ')}</span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1280px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
